import { useExplain } from '@vmos2/utils';
import assignWith from 'lodash/assignWith';
import isNil from 'lodash/isNil';
import type { ReactNode } from 'react';
import React, { createContext, useMemo, useContext, useState, useCallback } from 'react';

import { settingsParsers } from './settingsParsers';

const customizer = (objValue: string | undefined, srcValue: string) =>
  isNil(objValue) || objValue === '' ? srcValue : objValue;

const defaultStoreSettings = {};
const SettingsContext = createContext<Record<string, any> | undefined>(undefined);
const ShowNativeFooterContext = createContext({});

type Props = {
  children: ReactNode;
  storeSettings?: Record<string, string | never>;
  tenantSettings: Record<string, string>;
};

const SettingsProvider = ({ storeSettings = defaultStoreSettings, tenantSettings, children }: Props) => {
  const [showNativeFooter, setShowNativeFooter] = useState(false);

  const updateShowNativeFooter = useCallback((value: boolean) => {
    setShowNativeFooter(value);
  }, []);

  const nativeFooterValue = useMemo(
    () => ({ showNativeFooter, updateShowNativeFooter }),
    [showNativeFooter, updateShowNativeFooter]
  );

  const value = useMemo(() => {
    const mergedSettings: Record<string, string> = assignWith({}, storeSettings, tenantSettings, customizer);

    return Object.entries(settingsParsers).reduce(
      (acc, [key, [settingKey, parser]]) => ({
        ...acc,
        [key]: parser(mergedSettings[settingKey], mergedSettings, storeSettings, tenantSettings),
      }),
      {}
    );
  }, [storeSettings, tenantSettings]);

  const explainedSettings = useExplain(value, ['settings'], key => settingsParsers[key]?.[0]) || value;

  return (
    <SettingsContext.Provider value={explainedSettings}>
      <ShowNativeFooterContext.Provider value={nativeFooterValue}>{children}</ShowNativeFooterContext.Provider>
    </SettingsContext.Provider>
  );
};

const useSettings = () => {
  const context = useContext(SettingsContext);

  if (context === undefined) {
    throw new Error('useSettings must be within SettingsProvider');
  }

  return context;
};

const useShowNativeFooter = () => {
  const context = useContext(ShowNativeFooterContext);

  if (context === undefined) {
    throw new Error('useShowNativeFooter must be within SettingsProvider');
  }

  return context;
};

export { SettingsProvider, useSettings, useShowNativeFooter };
